import React from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 35px;
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 650px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const QuestionText = styled.h2`
  font-size: 28px;
  text-align: center;
  margin-bottom: 40px;
  color: white;
  font-weight: 600;
  line-height: 1.4;
  padding: 0 10px;
  position: relative;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 25px;
  margin-top: 20px;
  width: 100%;
  max-width: 500px;
  
  @media (max-width: 500px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const AnswerButton = styled.button<{ isGood?: boolean }>`
  background: ${(props) => (props.isGood === undefined 
    ? 'linear-gradient(45deg, rgba(76, 175, 80, 0.9), rgba(69, 160, 73, 0.9))' 
    : props.isGood 
      ? 'linear-gradient(45deg, rgba(76, 175, 80, 0.9), rgba(69, 160, 73, 0.9))' 
      : 'linear-gradient(45deg, rgba(244, 67, 54, 0.9), rgba(211, 47, 47, 0.9))')};
  color: white;
  font-size: 18px;
  padding: 16px 30px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: ${(props) => (props.isGood === undefined 
    ? '0 4px 20px rgba(76, 175, 80, 0.3)' 
    : props.isGood 
      ? '0 4px 20px rgba(76, 175, 80, 0.3)' 
      : '0 4px 20px rgba(244, 67, 54, 0.3)')};
  font-weight: 600;
  letter-spacing: 0.5px;
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  svg {
    width: 24px;
    height: 24px;
    transition: all 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${(props) => (props.isGood === undefined 
      ? '0 6px 25px rgba(76, 175, 80, 0.5)' 
      : props.isGood 
        ? '0 6px 25px rgba(76, 175, 80, 0.5)' 
        : '0 6px 25px rgba(244, 67, 54, 0.5)')};
        
    svg {
      transform: scale(1.2);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    background: rgba(204, 204, 204, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const NextButton = styled.button`
  background: linear-gradient(45deg, #9945FF, #14F195);
  color: white;
  font-size: 18px;
  padding: 16px 30px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 30px;
  box-shadow: 0 4px 20px rgba(153, 69, 255, 0.3);
  font-weight: 600;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(153, 69, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  margin-top: 30px;
  font-size: 22px;
  padding: 18px 24px;
  border-radius: 12px;
  background-color: ${(props) => (props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)')};
  color: ${(props) => (props.isCorrect ? '#4CAF50' : '#F44336')};
  text-align: center;
  border-left: 5px solid ${(props) => (props.isCorrect ? '#4CAF50' : '#F44336')};
  font-weight: 500;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid #14F195;
  border-left: 4px solid #9945FF;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// SVG Icons
const ThumbsUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
  </svg>
);

const ThumbsDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M22,3L1,12L22,21V14L7,12L22,10V3Z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9,16.17L4.83,12L3.41,13.41L9,19L21,7L19.59,5.59L9,16.17Z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
  </svg>
);

const TariffQuestion: React.FC = () => {
  const { 
    currentQuestion, 
    answerQuestion, 
    showFeedback, 
    isAnswerCorrect,
    nextQuestion 
  } = useGame();

  if (!currentQuestion) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        Loading next tariff question
      </LoadingContainer>
    );
  }

  return (
    <QuestionContainer>
      <QuestionText>{currentQuestion.question}</QuestionText>
      
      {!showFeedback ? (
        <ButtonsContainer>
          <AnswerButton isGood={true} onClick={() => answerQuestion(true)}>
            <ThumbsUpIcon />
            Good Tariff
          </AnswerButton>
          <AnswerButton isGood={false} onClick={() => answerQuestion(false)}>
            <ThumbsDownIcon />
            Bad Tariff
          </AnswerButton>
        </ButtonsContainer>
      ) : (
        <>
          <FeedbackMessage isCorrect={isAnswerCorrect || false}>
            {isAnswerCorrect ? <CheckIcon /> : <XIcon />}
            {isAnswerCorrect 
              ? "Correct! Your tariff score has increased." 
              : "Wrong! Your tariff score has decreased."}
          </FeedbackMessage>
          <NextButton onClick={nextQuestion}>
            Next Question
          </NextButton>
        </>
      )}
    </QuestionContainer>
  );
};

export default TariffQuestion;